import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import clsx from 'clsx';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { useAvatarUrl } from '../../hooks/useAvatarUrl';
import BrandLogo from '../BrandLogo';
import { CookieBanner } from './CookieConsent';
import { useMarketingAnalytics } from './useMarketingAnalytics';
import Avatar from '../ui/Avatar';
import ModuleIcon from './ModuleIcon';
import { useHydrated } from '../../hooks/useHydrated';

const navLinkClass = ({ isActive }) =>
  clsx(
    'text-sm hover:text-pub-navy',
    isActive ? 'font-semibold text-pub-blue-strong' : 'text-pub-gray-700',
  );

const mobileLinkClass = ({ isActive }) =>
  clsx(
    'block rounded-lg px-2 py-3 text-base hover:bg-pub-gray-50',
    isActive ? 'font-semibold text-pub-blue-strong' : 'font-medium text-pub-gray-700',
  );

/** Top-level marketing pages, shown in the desktop header and the mobile menu. */
const PAGES = [
  { to: '/', label: 'Home', end: true },
  { to: '/security', label: 'Security' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/blog', label: 'Blog' },
];

/** The MyDocUploads product family, shown in the header's Products mega-menu. */
const MODULES = [
  { slug: 'core', name: 'MyDocUploads Core', tagline: 'Secure file storage & sharing', to: '/products/core' },
  { slug: 'conversations', name: 'MyDocUploads Conversations', tagline: 'Contextual file conversations & decisions', to: '/products/conversations' },
  { slug: 'secure-drop', name: 'MyDocUploads Secure Drop', tagline: 'Send credentials & one-time secrets', to: '/products/secure-drop' },
  { slug: 'sign', name: 'MyDocUploads Sign', tagline: 'E-signatures on PDFs', to: '/products/sign' },
  { slug: 'flow', name: 'MyDocUploads Flow', tagline: 'Forms and client intake', to: '/products/flow' },
];

const OTHER_APPS = [
  { name: 'Feedback', href: 'https://www.myappfeedback.com/', icon: '/myfeedbackapp.png' },
  { name: 'QR Design', href: 'https://myqrcodedesign.com/', icon: '/myqrcodedesign.png' },
  { name: 'Consultra Cloud', href: 'https://www.consultracloud.com/', icon: '/consultracloud.png' },
  { name: 'Authority IQ', href: 'https://myauthorityiq.com/', icon: '/myauthorityiq.png' },
  { name: 'Marketing Companion', href: 'https://mymarketingcompanion.com/', icon: '/mymarketingcompanion.png' },
];

/**
 * Light marketing shell shared by landing, pricing, blog and security pages.
 * Deliberately separate from the dark
 * `AppLayout`/`AuthShell`.
 */
export default function PublicLayout({ children }) {
  const analytics = useMarketingAnalytics();
  const user = useAuthStore((state) => state.user);
  const hasSession = useAuthStore((state) => Boolean(state.accessToken));
  // Pre-rendered pages are the signed-out view; switch after hydration.
  const isAuthenticated = useHydrated() && hasSession;
  const avatarUrl = useAvatarUrl();
  const isPortalAdmin = user?.role === 'super_admin';
  const profileDestination = isPortalAdmin ? '/admin' : '/profile';
  const workspaceDestination = isPortalAdmin ? '/admin' : '/files';
  const displayName = user?.displayName || user?.email || 'Your';
  const [appsOpen, setAppsOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const appsRef = useRef(null);
  const productsRef = useRef(null);
  const mobileRef = useRef(null);

  useEffect(() => {
    const close = (event) => {
      if (event.key === 'Escape') {
        setAppsOpen(false);
        setProductsOpen(false);
        setMobileOpen(false);
        return;
      }
      if (event.type === 'mousedown') {
        if (!appsRef.current?.contains(event.target)) setAppsOpen(false);
        if (!productsRef.current?.contains(event.target)) setProductsOpen(false);
        if (!mobileRef.current?.contains(event.target)) setMobileOpen(false);
      }
    };
    document.addEventListener('keydown', close);
    document.addEventListener('mousedown', close);
    return () => {
      document.removeEventListener('keydown', close);
      document.removeEventListener('mousedown', close);
    };
  }, []);

  return (
    <div className="public-page flex min-h-screen flex-col">
      <header className="sticky top-0 z-50 border-b border-pub-gray-200 bg-white/85 backdrop-blur-md">
        <nav className="mx-auto flex h-20 w-full max-w-[1200px] items-center px-4 sm:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="MyDocUploads home">
            <BrandLogo className="h-8 w-auto sm:h-12" to={null} />
          </Link>
          <div className="hidden flex-1 items-center justify-center gap-6 lg:flex">
            <div ref={productsRef} className="relative">
              <button
                type="button"
                aria-expanded={productsOpen}
                aria-controls="products-mega-menu"
                onClick={() => setProductsOpen((open) => !open)}
                className={clsx(
                  'flex items-center gap-1 text-sm',
                  productsOpen ? 'font-semibold text-pub-blue-strong' : 'text-pub-gray-700 hover:text-pub-navy',
                )}
              >
                Modules
                <ChevronDown className={clsx('h-3.5 w-3.5 transition-transform', productsOpen && 'rotate-180')} />
              </button>
              {productsOpen && (
                <div
                  id="products-mega-menu"
                  className="absolute left-1/2 top-[calc(100%+16px)] z-50 w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 rounded-2xl border border-pub-gray-200 bg-white p-5 shadow-pub-card-strong"
                >
                  <p className="px-1 pb-3 text-xs font-semibold uppercase tracking-[.12em] text-pub-gray-500">
                    The MyDocUploads family
                  </p>
                  <div className="grid grid-cols-2 gap-1.5">
                    {MODULES.map(({ slug, name, tagline, to }) => (
                      <Link
                        key={to}
                        to={to}
                        onClick={() => setProductsOpen(false)}
                        className="flex items-start gap-3 rounded-xl px-3 py-3 transition hover:bg-pub-gray-50"
                      >
                        <ModuleIcon slug={slug} variant="tile" className="h-10 w-10" decorative />
                        <span>
                          <span className="block text-sm font-semibold text-pub-navy">{name}</span>
                          <span className="mt-0.5 block text-xs text-pub-gray-600">{tagline}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                  <div className="mt-2 border-t border-pub-gray-200 pt-3">
                    <Link
                      to="/pricing"
                      onClick={() => setProductsOpen(false)}
                      className="block rounded-xl px-3 py-2.5 text-sm font-semibold text-pub-blue-strong hover:bg-pub-gray-50"
                    >
                      See how the family fits together — view pricing →
                    </Link>
                  </div>
                </div>
              )}
            </div>
            {PAGES.map(({ to, label, end }) => (
              <NavLink key={to} to={to} end={end} className={navLinkClass}>
                {label}
              </NavLink>
            ))}
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-1 sm:gap-4">
            <div ref={appsRef} className="relative shrink-0">
              <button
                type="button"
                aria-label="Open other apps"
                aria-expanded={appsOpen}
                aria-controls="public-app-switcher"
                onClick={() => setAppsOpen((open) => !open)}
                className={clsx(
                  'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition',
                  appsOpen
                    ? 'bg-pub-gray-100 text-pub-navy'
                    : 'text-pub-gray-700 hover:bg-pub-gray-100 hover:text-pub-navy',
                )}
              >
                <span className="grid grid-cols-3 grid-rows-3 gap-[3px]" aria-hidden="true">
                  {Array.from({ length: 9 }, (_, index) => (
                    <span key={index} className="h-[4px] w-[4px] rounded-full bg-current" />
                  ))}
                </span>
                <span className="hidden sm:inline">Other apps</span>
                <ChevronDown
                  className={clsx('hidden h-3.5 w-3.5 transition-transform sm:block', appsOpen && 'rotate-180')}
                />
              </button>
              {appsOpen && (
                <div id="public-app-switcher" className="absolute right-0 top-[calc(100%+12px)] z-50 w-[292px] rounded-2xl border border-pub-gray-200 bg-white p-4 shadow-pub-card-strong">
                  <p className="px-1 pb-3 text-xs font-semibold text-pub-gray-500">Other apps</p>
                  <div className="grid grid-cols-3 gap-2">
                    {OTHER_APPS.map((app) => (
                      <a
                        key={app.href}
                        href={app.href}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => setAppsOpen(false)}
                        className="flex min-h-24 flex-col items-center rounded-xl px-2 py-3 text-center text-xs font-semibold leading-4 text-pub-gray-700 transition hover:bg-pub-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pub-blue"
                      >
                        <img src={app.icon} alt="" className="h-11 w-11 object-contain" />
                        <span className="mt-2">{app.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {isAuthenticated ? (
              <>
                <Link
                  to={profileDestination}
                  aria-label={`${displayName} profile`}
                  title={`${displayName} profile`}
                  className="rounded-full outline-none ring-pub-blue focus-visible:ring-2 focus-visible:ring-offset-2"
                >
                  <Avatar
                    name={displayName}
                    src={avatarUrl}
                    className="h-9 w-9 bg-pub-navy text-xs font-bold text-white shadow-sm"
                  />
                </Link>
                <Link
                  to={workspaceDestination}
                  className="hidden rounded-full bg-pub-blue px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-pub-blue-dark lg:inline-flex"
                >
                  Open workspace
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="rounded-lg px-3 py-2 text-sm font-semibold text-pub-gray-700 hover:bg-pub-gray-100 sm:px-4"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="hidden rounded-full bg-pub-blue px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-pub-blue-dark lg:inline-flex"
                >
                  Get started
                </Link>
              </>
            )}
            {/* Below lg the centre nav and the CTA are hidden — this menu carries them. */}
            <div ref={mobileRef} className="lg:hidden">
              <button
                type="button"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileOpen}
                aria-controls="public-mobile-menu"
                onClick={() => setMobileOpen((open) => !open)}
                className="rounded-lg p-2 text-pub-gray-700 transition hover:bg-pub-gray-100 hover:text-pub-navy"
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              {mobileOpen && (
                <div
                  id="public-mobile-menu"
                  className="absolute inset-x-0 top-full max-h-[calc(100dvh-5rem)] overflow-y-auto border-b border-pub-gray-200 bg-white px-4 pb-6 pt-2 shadow-pub-card-strong sm:px-6"
                >
                  <div className="mx-auto max-w-[1200px]">
                    {PAGES.map(({ to, label, end }) => (
                      <NavLink
                        key={to}
                        to={to}
                        end={end}
                        onClick={() => setMobileOpen(false)}
                        className={mobileLinkClass}
                      >
                        {label}
                      </NavLink>
                    ))}
                    <p className="mt-3 border-t border-pub-gray-200 px-2 pb-2 pt-4 text-xs font-semibold uppercase tracking-[.12em] text-pub-gray-500">
                      Modules
                    </p>
                    <div className="grid gap-1 sm:grid-cols-2">
                      {MODULES.map(({ slug, name, to }) => (
                        <Link
                          key={to}
                          to={to}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-3 rounded-xl px-2 py-2 transition hover:bg-pub-gray-50"
                        >
                          <ModuleIcon slug={slug} variant="tile" className="h-8 w-8" decorative />
                          <span className="text-sm font-semibold text-pub-navy">{name}</span>
                        </Link>
                      ))}
                    </div>
                    <Link
                      to={isAuthenticated ? workspaceDestination : '/register'}
                      onClick={() => setMobileOpen(false)}
                      className="mt-5 flex justify-center rounded-full bg-pub-blue px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-pub-blue-dark"
                    >
                      {isAuthenticated ? 'Open workspace' : 'Get started'}
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-pub-gray-200 bg-pub-gray-50 px-6 py-14 text-sm text-pub-gray-600">
        <div className="mx-auto grid max-w-[1200px] gap-10 md:grid-cols-[1.3fr_1fr_1fr]">
          <div>
            <BrandLogo className="h-11 w-auto" />
            <p className="mt-4 max-w-sm leading-6">
              A focused document workspace for uploads, shares, comments, signatures, and team
              access.
            </p>
            <div className="mt-5" role="group" aria-label="Other apps">
              <p className="text-xs font-semibold text-pub-navy">Other apps</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {OTHER_APPS.map((app) => (
                  <a
                    key={app.href}
                    href={app.href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={app.name}
                    title={app.name}
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-pub-gray-200 bg-white transition hover:bg-pub-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pub-blue"
                  >
                    <img src={app.icon} alt="" className="h-5 w-5 object-contain" />
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[.12em] text-pub-navy">Product</p>
            <div className="mt-4 flex flex-col gap-3 font-medium text-pub-gray-700">
              <Link to="/" className="hover:text-pub-navy">Home</Link>
              <Link to="/security" className="hover:text-pub-navy">Security</Link>
              <Link to="/pricing" className="hover:text-pub-navy">Pricing</Link>
              <Link to="/blog" className="hover:text-pub-navy">Blog</Link>
              {isAuthenticated ? (
                <Link to={workspaceDestination} className="hover:text-pub-navy">Open workspace</Link>
              ) : (
                <Link to="/login" className="hover:text-pub-navy">Sign in</Link>
              )}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[.12em] text-pub-navy">Company</p>
            <p className="mt-4 max-w-xs leading-6">
              Upload, request, share, sign, and track every project file with MyDocUploads.
            </p>
          </div>
        </div>
        <div className="mx-auto mt-10 flex max-w-[1200px] flex-col gap-3 border-t border-pub-gray-200 pt-6 text-xs text-pub-gray-500 md:flex-row md:items-center md:justify-between">
          <p>
            © {new Date().getFullYear()} MyDocUploads. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            {analytics.canChange && (
              <button type="button" onClick={analytics.reopen} className="font-medium hover:text-pub-navy">
                Cookie settings
              </button>
            )}
            <a href="mailto:sales@mydocuploads.com" className="font-medium hover:text-pub-navy">sales@mydocuploads.com</a>
          </div>
        </div>
      </footer>
      {analytics.showBanner && (
        <CookieBanner onAccept={analytics.accept} onDecline={analytics.decline} />
      )}
    </div>
  );
}
